class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
document.addEventListener('DOMContentLoaded', function() {
  // Initialize MutationObserver to monitor changes in the cart
  const cartObserver = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.type === 'childList' || mutation.type === 'subtree') {
        attachAddToCartEventListeners(); // Reattach event listeners after any DOM changes
      }
    });
  });
  // Function to observe cart drawer for changes
  function observeCartDrawer() {
    const cartDrawer = document.querySelector("#CartDrawer");
    if (cartDrawer) {
      cartObserver.observe(cartDrawer, { childList: true, subtree: true });
    }
  }
  // Function to reload the cart drawer
  function reloadCartDrawer() {
    const cartDrawer = document.querySelector("#CartDrawer");
    if (cartDrawer) {
      fetch('/cart') // Fetch the cart content directly from Shopify's /cart endpoint
      .then(response => response.text())
      .then(html => {
        // Create a temporary DOM parser to extract the #CartDrawer content
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        // Extract the new #CartDrawer content
        const newCartDrawer = doc.querySelector("#CartDrawer");

        if (newCartDrawer) {
          cartDrawer.innerHTML = newCartDrawer.innerHTML;
        }
        // Re-attach the event listeners after updating the cart drawer
        attachAddToCartEventListeners();
      })
      .catch(error => {
        console.error("Error loading CartDrawer:", error);
      });
    }
  }
  // Function to attach event listeners to Add to Cart buttons
  function attachAddToCartEventListeners() {
    const addToCartButtons = document.querySelectorAll('.add-to-cart-button');
    
    // Ensure we are not attaching multiple event listeners by removing any old listeners first
    addToCartButtons.forEach(button => {
      button.removeEventListener('click', addToCartHandler); // Remove previous listeners
      button.addEventListener('click', addToCartHandler); // Add new listener
    });
  }
  // Function to handle Add to Cart button click event
  function addToCartHandler(event) {
    const form = event.target.closest('form');
    const productId = form.querySelector('input[name="id"]').value;
    // Check if the product is available before adding to cart
    const isAvailable = event.target.textContent.trim() !== 'Out of Stock';
    if (!isAvailable) {
      return;
    }
    // Add product to cart using Shopify's AJAX API
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id: productId, quantity: 1 }),
    })
    .then(response => response.json())
    .then(data => {
      // After adding the product, update the cart drawer content
      reloadCartDrawer();
    })
    .catch(error => console.error('Error adding product to cart:', error));
  }
  // Wait for the DOM to be fully loaded before attaching event listeners
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      attachAddToCartEventListeners();
      observeCartDrawer(); // Start observing the cart drawer for changes
    });
  } else {
    attachAddToCartEventListeners(); // If DOM is already loaded, attach listeners
    observeCartDrawer(); // Start observing the cart drawer for changes
  }
  // Optional: Close the cart drawer when clicking outside (you can customize this)
  const closeBtn = document.querySelector('.cart-slider-close');
  if (closeBtn) {
    closeBtn.addEventListener('click', function() {
      const cartSlider = document.querySelector('.cart_slider');
      if (cartSlider) {
        cartSlider.style.display = 'none';
      }
    });
  }
});